import { useState } from 'react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, FileText, CreditCard, Mail, Calendar, CheckCircle2,
  Edit3, Trash2, ExternalLink, Copy, Clock, Building2,
  RefreshCw, Send, Wallet, MapPin, CheckCircle, HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { updateDocument, updateDocumentStatus, deleteDocument, createPaymentCheckout, getPaymentStatus, generateAIEmail, sendEmail } from '../services/api';

const DocumentDrawer = ({ document: doc, open, onClose, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [processing, setProcessing] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState(null);
  const [generatedEmail, setGeneratedEmail] = useState(null);
  const [generatingEmail, setGeneratingEmail] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [showExtensionOptions, setShowExtensionOptions] = useState(false);
  const [showInquiryOptions, setShowInquiryOptions] = useState(false);
  const [selectedExtensionDays, setSelectedExtensionDays] = useState(null);
  const [selectedInquiryType, setSelectedInquiryType] = useState(null);

  // Extension options
  const extensionOptions = [
    { days: 7, label: '7 Tage' },
    { days: 14, label: '14 Tage' },
    { days: 21, label: '21 Tage' },
  ];

  // Inquiry options with icons
  const inquiryOptions = [
    { type: 'wrong_amount', label: 'Betrag ist falsch', icon: Wallet },
    { type: 'wrong_address', label: 'Adresse ist falsch', icon: MapPin },
    { type: 'duplicate', label: 'Rechnung bereits bezahlt', icon: CheckCircle },
    { type: 'missing_details', label: 'Details fehlen', icon: FileText },
    { type: 'wrong_period', label: 'Falscher Zeitraum', icon: Calendar },
    { type: 'general', label: 'Allgemeine Rückfrage', icon: HelpCircle },
  ];

  if (!open || !doc) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const handleEdit = () => {
    setEditData({
      sender_name: doc.sender_name || '',
      amount: doc.amount || '',
      due_date: doc.due_date || '',
      invoice_no: doc.invoice_no || '',
      reference: doc.reference || '',
      iban: doc.iban || '',
      summary: doc.summary || '',
      doc_type: doc.doc_type || 'Rechnung'
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setProcessing(true);
    try {
      await updateDocument(doc.id, editData);
      toast.success('Dokument aktualisiert');
      setEditing(false);
      onUpdate?.();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setProcessing(false);
    }
  };

  const handleStatusChange = async (status) => {
    setProcessing(true);
    try {
      await updateDocumentStatus(doc.id, status);
      toast.success('Status aktualisiert');
      onUpdate?.();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Dokument wirklich löschen?')) return;
    
    setProcessing(true);
    try {
      await deleteDocument(doc.id);
      toast.success('Dokument gelöscht');
      onClose();
      onUpdate?.();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setProcessing(false);
    }
  };

  const handlePayment = async () => {
    if (!doc.amount || doc.amount <= 0) {
      toast.error('Kein Betrag vorhanden');
      return;
    }

    setProcessing(true);
    try {
      const response = await createPaymentCheckout(doc.id, doc.amount);
      window.open(response.data.checkout_url, '_blank');
      toast.success('Zahlungsseite geöffnet');
    } catch (error) {
      toast.error('Fehler bei der Zahlung');
    } finally {
      setProcessing(false);
    }
  };

  const handleGenerateEmail = async (templateType, options = {}) => {
    setGeneratingEmail(true);
    setEmailTemplate(templateType);
    setShowExtensionOptions(false);
    setShowInquiryOptions(false);
    try {
      const response = await generateAIEmail(doc.id, templateType, options);
      setGeneratedEmail(response.data);
      // Pre-fill recipient from document sender - use extracted email if available
      setRecipientName(doc.sender_name || '');
      setRecipientEmail(doc.sender_email || doc.extracted_data?.sender_email || '');
    } catch (error) {
      toast.error('Fehler bei der E-Mail-Generierung');
    } finally {
      setGeneratingEmail(false);
    }
  };

  const handleExtensionSelect = (days) => {
    setSelectedExtensionDays(days);
    handleGenerateEmail('extension', { days });
  };

  const handleInquirySelect = (type) => {
    setSelectedInquiryType(type);
    handleGenerateEmail('inquiry', { inquiry_type: type });
  };

  const handleSendEmail = async () => {
    if (!recipientEmail) {
      toast.error('Bitte Empfänger-E-Mail eingeben');
      return;
    }
    if (!generatedEmail) {
      toast.error('Bitte zuerst eine E-Mail generieren');
      return;
    }
    
    setSendingEmail(true);
    try {
      // Convert plain text to HTML
      const htmlBody = `<div style="font-family: Arial, sans-serif; line-height: 1.6;">${generatedEmail.body.replace(/\n/g, '<br>')}</div>`;
      await sendEmail(recipientEmail, recipientName, generatedEmail.subject, htmlBody);
      toast.success('E-Mail erfolgreich gesendet!');
      setGeneratedEmail(null);
      setRecipientEmail('');
      setRecipientName('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'E-Mail konnte nicht gesendet werden';
      toast.error(errorMsg);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCopyEmail = () => {
    if (generatedEmail) {
      navigator.clipboard.writeText(`Betreff: ${generatedEmail.subject}\n\n${generatedEmail.body}`);
      toast.success('E-Mail in Zwischenablage kopiert');
    }
  };

  const statusOptions = [
    { value: 'urgent', label: 'Dringend', color: 'red' },
    { value: 'pipeline', label: 'In der Pipeline', color: 'amber' },
    { value: 'progress', label: 'Wird erledigt', color: 'blue' },
    { value: 'done', label: 'Erledigt', color: 'green' },
  ];

  const docTypes = ['Rechnung', 'Mahnung', 'Angebot', 'Vertrag', 'Behördenschreiben', 'Blitzer', 'Sonstiges'];

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full max-w-xl glass-panel shadow-2xl z-50 flex flex-col"
            data-testid="document-drawer"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  doc.doc_type === 'Rechnung' ? 'bg-blue-500/20 text-blue-400' :
                  doc.doc_type === 'Mahnung' ? 'bg-red-500/20 text-red-400' :
                  'bg-white/10 text-gray-400'
                }`}>
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {doc.sender_name || 'Dokument'}
                  </h2>
                  <p className="text-sm text-gray-500">{doc.doc_type}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-xl transition-colors text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {/* Status Bar */}
              <div className="p-6 border-b border-white/10">
                <Label className="text-sm font-medium text-gray-500 mb-2 block">Status</Label>
                <div className="flex gap-2 flex-wrap">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleStatusChange(option.value)}
                      disabled={processing}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        doc.status === option.value
                          ? option.color === 'red' ? 'bg-red-500/20 text-red-400 ring-2 ring-red-500/50' :
                            option.color === 'amber' ? 'bg-amber-500/20 text-amber-400 ring-2 ring-amber-500/50' :
                            option.color === 'blue' ? 'bg-blue-500/20 text-blue-400 ring-2 ring-blue-500/50' :
                            'bg-emerald-500/20 text-emerald-400 ring-2 ring-emerald-500/50'
                          : 'bg-white/5 text-gray-400 hover:bg-white/10'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Main Actions */}
              <div className="p-6 border-b border-white/10">
                <Label className="text-sm font-medium text-gray-500 mb-3 block">Aktionen</Label>
                <div className="grid grid-cols-3 gap-3">
                  <Button
                    onClick={handlePayment}
                    disabled={processing || !doc.amount}
                    className="bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white rounded-xl flex-col h-auto py-4"
                    data-testid="pay-btn"
                  >
                    <CreditCard className="w-5 h-5 mb-1" />
                    <span className="text-xs">Bezahlen</span>
                  </Button>
                  
                  <Button
                    onClick={() => setShowExtensionOptions(!showExtensionOptions)}
                    disabled={generatingEmail}
                    variant="outline"
                    className={`rounded-xl flex-col h-auto py-4 bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:text-white ${showExtensionOptions ? 'ring-2 ring-amber-500/50' : ''}`}
                  >
                    <Clock className="w-5 h-5 mb-1" />
                    <span className="text-xs">Frist</span>
                  </Button>
                  
                  <Button
                    onClick={() => setShowInquiryOptions(!showInquiryOptions)}
                    disabled={generatingEmail}
                    variant="outline"
                    className={`rounded-xl flex-col h-auto py-4 bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:text-white ${showInquiryOptions ? 'ring-2 ring-blue-500/50' : ''}`}
                  >
                    <Mail className="w-5 h-5 mb-1" />
                    <span className="text-xs">Rückfrage</span>
                  </Button>
                </div>

                {/* Extension Options */}
                {showExtensionOptions && (
                  <div className="mt-4 p-4 bg-amber-500/10 rounded-xl border border-amber-500/20">
                    <Label className="text-sm font-medium text-amber-400 mb-3 block">Um wie viele Tage verlängern?</Label>
                    <div className="flex gap-2">
                      {extensionOptions.map((option) => (
                        <button
                          key={option.days}
                          onClick={() => handleExtensionSelect(option.days)}
                          disabled={generatingEmail}
                          className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                            selectedExtensionDays === option.days
                              ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
                              : 'bg-white/10 text-gray-300 hover:bg-white/20'
                          }`}
                        >
                          {generatingEmail && selectedExtensionDays === option.days ? (
                            <RefreshCw className="w-4 h-4 animate-spin mx-auto" />
                          ) : (
                            option.label
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Inquiry Options */}
                {showInquiryOptions && (
                  <div className="mt-4 p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
                    <Label className="text-sm font-medium text-blue-400 mb-3 block">Was ist das Problem?</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {inquiryOptions.map((option) => (
                        <button
                          key={option.type}
                          onClick={() => handleInquirySelect(option.type)}
                          disabled={generatingEmail}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-left ${
                            selectedInquiryType === option.type
                              ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                              : 'bg-white/10 text-gray-300 hover:bg-white/20'
                          }`}
                        >
                          {generatingEmail && selectedInquiryType === option.type ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <option.icon className="w-4 h-4" />
                          )}
                          <span className="truncate">{option.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Generated Email */}
              {generatedEmail && (
                <div className="p-6 border-b border-white/10 bg-indigo-500/5">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium text-white">Generierte E-Mail</Label>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleCopyEmail}
                        className="h-8 text-gray-400 hover:text-white hover:bg-white/10"
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Kopieren
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setGeneratedEmail(null); setRecipientEmail(''); setRecipientName(''); }}
                        className="h-8 text-gray-400 hover:text-white hover:bg-white/10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Recipient Input */}
                  <div className="mb-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-gray-500">Empfänger-Name</Label>
                        <Input
                          value={recipientName}
                          onChange={(e) => setRecipientName(e.target.value)}
                          placeholder="Max Mustermann"
                          className="mt-1 bg-white/5 border-white/10 text-white h-9 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Empfänger-E-Mail *</Label>
                        <Input
                          type="email"
                          value={recipientEmail}
                          onChange={(e) => setRecipientEmail(e.target.value)}
                          placeholder="empfaenger@beispiel.de"
                          className="mt-1 bg-white/5 border-white/10 text-white h-9 text-sm"
                          data-testid="recipient-email-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-[#1A1A24] rounded-xl p-4 border border-white/10 mb-4">
                    <p className="font-medium text-sm mb-2 text-white">Betreff: {generatedEmail.subject}</p>
                    <p className="text-sm text-gray-400 whitespace-pre-wrap">{generatedEmail.body}</p>
                  </div>
                  
                  {/* Send Button */}
                  <Button
                    onClick={handleSendEmail}
                    disabled={sendingEmail || !recipientEmail}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl"
                    data-testid="send-email-btn"
                  >
                    {sendingEmail ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Wird gesendet...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        E-Mail senden
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Details */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-sm font-medium text-gray-500">Details</Label>
                  {!editing ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleEdit}
                      className="h-8 text-gray-400 hover:text-white hover:bg-white/10"
                    >
                      <Edit3 className="w-4 h-4 mr-1" />
                      Bearbeiten
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(false)}
                        className="h-8 text-gray-400 hover:text-white hover:bg-white/10"
                      >
                        Abbrechen
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={processing}
                        className="h-8 bg-gradient-to-r from-indigo-500 to-violet-500 text-white"
                      >
                        {processing ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Speichern'}
                      </Button>
                    </div>
                  )}
                </div>

                {editing ? (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs text-gray-500">Dokumenttyp</Label>
                      <Select
                        value={editData.doc_type}
                        onValueChange={(v) => setEditData({ ...editData, doc_type: v })}
                      >
                        <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A24] border-white/10">
                          {docTypes.map((type) => (
                            <SelectItem key={type} value={type} className="text-gray-300 focus:bg-white/10 focus:text-white">{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Absender</Label>
                      <Input
                        value={editData.sender_name}
                        onChange={(e) => setEditData({ ...editData, sender_name: e.target.value })}
                        className="mt-1 bg-white/5 border-white/10 text-white"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-gray-500">Betrag (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editData.amount}
                          onChange={(e) => setEditData({ ...editData, amount: parseFloat(e.target.value) })}
                          className="mt-1 bg-white/5 border-white/10 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Fälligkeitsdatum</Label>
                        <Input
                          type="date"
                          value={editData.due_date}
                          onChange={(e) => setEditData({ ...editData, due_date: e.target.value })}
                          className="mt-1 bg-white/5 border-white/10 text-white"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-gray-500">Rechnungsnummer</Label>
                        <Input
                          value={editData.invoice_no}
                          onChange={(e) => setEditData({ ...editData, invoice_no: e.target.value })}
                          className="mt-1 bg-white/5 border-white/10 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">IBAN</Label>
                        <Input
                          value={editData.iban}
                          onChange={(e) => setEditData({ ...editData, iban: e.target.value })}
                          className="mt-1 bg-white/5 border-white/10 text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Referenz/Verwendungszweck</Label>
                      <Input
                        value={editData.reference}
                        onChange={(e) => setEditData({ ...editData, reference: e.target.value })}
                        className="mt-1 bg-white/5 border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Zusammenfassung</Label>
                      <Textarea
                        value={editData.summary}
                        onChange={(e) => setEditData({ ...editData, summary: e.target.value })}
                        className="mt-1 bg-white/5 border-white/10 text-white"
                        rows={3}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <p className="text-xs text-gray-500 mb-1">Betrag</p>
                        <p className="text-xl font-bold tabular-nums text-white">{formatCurrency(doc.amount)}</p>
                      </div>
                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <p className="text-xs text-gray-500 mb-1">Fälligkeit</p>
                        <p className="text-lg font-medium text-white">
                          {doc.due_date ? new Date(doc.due_date).toLocaleDateString('de-DE') : '—'}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {doc.sender_name && (
                        <div>
                          <p className="text-xs text-gray-500">Absender</p>
                          <p className="font-medium text-white">{doc.sender_name}</p>
                        </div>
                      )}
                      {doc.invoice_no && (
                        <div>
                          <p className="text-xs text-gray-500">Rechnungsnummer</p>
                          <p className="font-medium font-mono text-white">{doc.invoice_no}</p>
                        </div>
                      )}
                      {doc.iban && (
                        <div>
                          <p className="text-xs text-gray-500">IBAN</p>
                          <p className="font-medium font-mono text-sm text-white">{doc.iban}</p>
                        </div>
                      )}
                      {doc.reference && (
                        <div>
                          <p className="text-xs text-gray-500">Verwendungszweck</p>
                          <p className="font-medium text-white">{doc.reference}</p>
                        </div>
                      )}
                      {doc.summary && (
                        <div>
                          <p className="text-xs text-gray-500">Zusammenfassung</p>
                          <p className="text-gray-300">{doc.summary}</p>
                        </div>
                      )}
                    </div>

                    {doc.confidence && (
                      <div className="mt-4 p-3 bg-white/5 rounded-xl border border-white/10">
                        <p className="text-xs text-gray-500 mb-1">KI-Konfidenz</p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                doc.confidence > 0.8 ? 'bg-emerald-500' :
                                doc.confidence > 0.5 ? 'bg-amber-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${doc.confidence * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-white">
                            {Math.round(doc.confidence * 100)}%
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-white/10 flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={handleDelete}
                disabled={processing}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Löschen
              </Button>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleStatusChange('done')}
                  disabled={processing || doc.status === 'done'}
                  className="rounded-xl bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:text-white"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Als erledigt markieren
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DocumentDrawer;
